import * as React from 'react';
import * as types from 'modules/types/smSupplierManagement';
import { ColProps } from 'modules/types/commons/form';
import { FilterOptionsArray } from 'modules/types/commons/filter';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { Form, Input, Select, Radio, AutoComplete, DatePicker, TreeSelect, Card } from '@beehive/components';
import { BASIC_INFO_KEYS, TRANSLATE_MAPPING_KEYS } from '../utils';
import { isBlank } from 'utils/object';
import { filterByString } from 'utils/array';
import PropTypes from 'prop-types';
import supplierMessages from 'messages/Models/Supplier';
import titleMessages from 'messages/CompanyManagement/CardTitle';

const messages = { ...supplierMessages, ...titleMessages };

const FORM_KEYS = Object.keys(BASIC_INFO_KEYS);

export interface FormItmeType {
  label: string;
  required?: boolean;
  componentClass: string;
  componentProps: any;
  optionsKey?: string;
  itemColProps?: ColProps;
  show?: boolean;
}

export interface HandleChagneParams {
  key: string;
  value: any;
  item?: any;
}

export interface RenderFromParams {
  key: string;
  value: React.ReactElement<any> | null | string;
  options?: Partial<Record<types.DetailOptionsKeys, FilterOptionsArray>>;
  data: any;
  otherProps?: any;
  handleChange: (options: HandleChagneParams) => any;
  show?: boolean;
}

export type ChangeEvent = React.ChangeEvent<HTMLInputElement>;

export interface CustomFormKeys { [k: string]: Partial<FormItmeType>; }

export interface BasicInfoProps {
  data: types.SupplierMangementDetail;
  onChange: any;
  customFormKeys?: CustomFormKeys;
  showError?: boolean;
  isEditNew?: boolean;
}

const renderForm: React.SFC<RenderFromParams> = (props) => {
  const { key, value, show, options, handleChange, data } = props;
  const item: FormItmeType = data[key];
  const otherProps = item.componentProps || {};
  Object.assign(otherProps, props.otherProps);
  let selectOptions = _.get(options, `${item.optionsKey}`, []);

  const showRender = <span>{isBlank(value) ? '--' : value}</span>;
  if (show) {
    return showRender;
  }
  switch (item.componentClass) {
    case 'autocomplete':
      const others: any = {};
      if (key === 'referrer_name' && selectOptions) {
        selectOptions = selectOptions.map((i: any) => ({ ...i, value: i.id }));
        others.searchHandler = (searchValue: string, items: any) => {
          return filterByString(items, searchValue, (v: any, reg: RegExp) => reg.test(v.name) || reg.test(v.email));
        };
      }
      return (
        <AutoComplete
          items={selectOptions}
          value={value}
          onChange={(evt: any) => {
            let v = evt.target.value;
            let selectItem = evt.selectItem;
            if (selectItem) {
              v = selectItem.name;
            } else {
              selectItem = {};
            }
            handleChange({ key, value: v, item: selectItem });
          }}
          {...others}
        />
      );
    case 'datePicker': {
      const val = (value && typeof value === 'string') ? moment(value) : null;
      return (
        <DatePicker
          {...otherProps}
          size="large"
          value={val}
          format="YYYY-MM-DD"
          onChange={(value: Moment) => {
            handleChange({ key, value: value ? value.format('YYYY-MM-DD') : '' });
          }}
        />
      );
    }
    case 'input':
      return (
        <Input
          {...otherProps}
          value={value}
          onChange={(evt: ChangeEvent) => handleChange({ key, value: evt.target.value })}
        />
      );
    case 'select':
      return (
        <Select
          options={selectOptions}
          value={value}
          mode="select"
          onChange={(v: any) => {
            const itm = selectOptions.find((i: any) => i.value === v);
            handleChange({ key, value: v, item: itm });
          }}
        />
      );
    case 'treeSelect':
      return (
        <TreeSelect
          options={selectOptions}
          value={value}
          mode="select"
          onChange={(v: any) => {
            handleChange({ key, value: v });
          }}
          {...otherProps}
        />
      );
    case 'radio':
      const items = options && item.optionsKey
        ? options[item.optionsKey].map((i: any) => ({ label: i.name, value: i.value })) : [];
      return (
        <Radio.Group
          onChange={(evt: ChangeEvent) => handleChange({ key, value: evt.target.value })}
          value={value}
          items={items}
        />
      );
    case 'textarea':
      return (
        <Input.TextArea
          maxHeight={71}
          minHeight={32}
          autosize
          value={isBlank(value) ? '' : value}
          onChange={(evt: ChangeEvent) => handleChange({ key, value: evt.target.value })}
          {...otherProps}
        />
      );
    default:
      return showRender;
  }
};

class BasicInfo extends React.Component<BasicInfoProps, any> {
  public static defaultProps: Partial<BasicInfoProps> = {
    customFormKeys: _.omit(BASIC_INFO_KEYS, ['mpc_no']),
  };

  public static contextTypes = {
    intl: PropTypes.object,
  };

  private formRef: React.RefObject<typeof Form>;

  constructor(props: BasicInfoProps) {
    super(props);
    this.formRef = React.createRef();
  }

  public shouldComponentUpdate(nextProps: BasicInfoProps) {
    if (this.props.showError !== nextProps.showError) {
      return true;
    }
    const pickKeys = [...Object.keys(BASIC_INFO_KEYS),
      'detailError.messages', 'data.loading', 'data.loaded', 'change_detail'];
    if (_.isEqual(_.pick(this.props.data, pickKeys), _.pick(nextProps.data, pickKeys))) {
      return false;
    }
    return true;
  }

  // 修改内容
  private handleChange = (params: HandleChagneParams) => {
    const { onChange, data, data: { change_detail = {} } } = this.props;
    const { key, value } = params;
    const error = _.get(data, `detailError.messages.${key}`);
    const newDetailError = _.cloneDeep(data.detailError);
    if (error) {
      delete newDetailError.messages[key];
    }
    const newData = {
      ...data,
      detailError: { ...newDetailError },
      change_detail: {
        ...change_detail,
        supplier: {
          ..._.get(change_detail, 'supplier', {}),
          [key]: value,
        },
      },
    };
    onChange(newData);
  }

  public isFormValid = (): boolean => {
    if (this.formRef.current) {
      return this.formRef.current.valid();
    }
    return true;
  }

  public render() {
    const { intl } = this.context;
    const { data, data: { change_detail = {} }, customFormKeys = {}, showError, isEditNew = false } = this.props;
    const { options, loading, loaded } = data;
    const errorMsg = _.pick(_.get(data, 'detailError.messages'), FORM_KEYS);
    const changedData = _.get(change_detail, 'supplier', {});
    return (
      <Card
        titleNode={intl.formatMessage(messages.basicInfo)}
        loading={loading || !loaded}
        className={`basic-info ${isEditNew ? 'edit-new' : ''}`}
      >
        <Form
          isLabelVertical
          errorMsg={errorMsg}
          showError={showError}
          mode="edit"
          ref={this.formRef}
          showClientError
        >
          {
            Object.keys(customFormKeys).map((key: string) => {
              const item = BASIC_INFO_KEYS[key];
              let value = data[key];
              if (key in changedData) {
                value = changedData[key];
              }
              const itemColProps = customFormKeys[key].itemColProps || { sm: 12, xxl: 8 };
              const show = customFormKeys[key].show || false;
              const tip = !show && item.tip && intl.formatMessage(messages[item.tip]);

              if (show && TRANSLATE_MAPPING_KEYS[key]) {
                value = data[TRANSLATE_MAPPING_KEYS[key].mapping_key];
              }

              return (
                <Form.Item
                  className={key}
                  itemKey={key}
                  label={item.label && intl.formatMessage(messages[item.label])}
                  required={!show && !!item.required}
                  key={key}
                  itemColProps={itemColProps}
                  tip={tip}
                >
                  {renderForm({
                    key, value, show, options,
                    data: BASIC_INFO_KEYS, handleChange: this.handleChange,
                  })}
                </Form.Item>
              );
            })
          }
        </Form>
      </Card>
    );
  }
}

export default BasicInfo;
export { renderForm };
